import { mapActions, mapGetters } from "vuex";
import toast from "@/utils/toast";
import { cleanObject } from "./utils/utils";

export default {
  name: "IdpUnEnrollmentReportPage",

  components: {},
  data() {
    return {
      filters: {
        page: 1,
        pageSize: 10,
        order: "desc",
        field: "updated_at",
        search: "",
        learning_type: "structured_learning",
        idp_period_id: null,
        all: false,
        nik: null,
        training_scope: null,
        training_category: null,
        platform: null,
        training_method_id: null,
        training_type: null,
      },
      position: null,
      isLoading: false,
      isLoadingEmployeeList: false,
      isModalLoading: false,
      isLoadingSummary: false,
      _searchTimer: null,
      _employeeSearchTimer: null,
      _employeeListSearchTimer: null,
      employeeListSearch: "",
      showModal: false,
      modalData: null,
      idpPeriodOptions: [],
      activeFiltersCount: 0,
      selectedRows: [],
      isLoadingApprove: false,
      showConfirmationSubmitModal: false,
      showRejectConfirmationModal: false,
      isSubmitting: false,
    };
  },

  computed: {
    ...mapGetters("idpEnrollment", [
      "idpLearningReport",
      "summaryIdpLearningReport",
      "trainingMethods",
      "trainingTypes",
      "competencies",
      "subordinates",
    ]),

    showingFrom() {
      if (!this.idpLearningReport.totalItems) return 0;
      return (this.filters.page - 1) * this.filters.pageSize + 1;
    },

    showingTo() {
      const to = this.filters.page * this.filters.pageSize;
      return to > this.idpLearningReport.totalItems ? this.idpLearningReport.totalItems : to;
    },

    selectedSubordinate() {
      return this.subordinates.find((sub) => String(sub.nik) === String(this.filters.nik)) || null;
    },

    selectedPeriodOption: {
      get() {
        return this.idpPeriodOptions.find((option) => String(option.value) === String(this.filters.idp_period_id)) || null;
      },
      set(option) {
        this.filters.idp_period_id = option?.value || null;
      },
    },

    selectedEmployeeName() {
      return this.selectedSubordinate?.name || "-";
    },

    pendingApprovalCount() {
      return this.showEmployeeList ? this.employeeListTotalSubmissions : this.idpLearningReport?.totalItems || 0;
    },

    showEmployeeList() {
      return !this.filters.nik;
    },

    employeeListTotalSubmissions() {
      return this.subordinates.reduce((total, item) => total + Number(item.total_learning || 0), 0);
    },
  },

  watch: {
    filters: {
      deep: true,
      handler(newFilters) {
        this.updateUrlQuery(newFilters);
      },
    },

    async "filters.idp_period_id"() {
      this.filters.page = 1;

      await this.loadApprovalSubordinates(this.employeeListSearch, true);

      if (!this.filters.nik) {
        this.resetIdpLearnings();
        return;
      }

      this.loadData();
    },

    "filters.nik"(newVal) {
      this.filters.page = 1;

      this.position = this.selectedSubordinate?.position || null;

      if (!newVal) {
        this.resetIdpLearnings();
        return;
      }

      this.loadData();

      this.loadSummaryLearning({ nik: newVal });
    },

    "$route.query": {
      handler() {
        this.syncFiltersFromQuery();
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions("idpEnrollment", [
      "getIdpLearningReport",
      "getStructuredLearningDetail",
      "getSummaryIdpLearningReport",
      "getIdpPeriod",
      "getTrainingMethods",
      "getTrainingTypes",
      "getCompetencies",
      "getSubordinates",
      "resetIdpLearnings",
      "approveIdpLearning",
    ]),

    showFilterModal() {
      this.$bvModal.show("modal-filter");
    },

    getOptionLabel(options = [], value) {
      const option = options.find((item) => String(item.value) === String(value));
      return option?.label || option?.text || value || "-";
    },

    buildQueryFromFilters() {
      const raw = {
        page: this.filters.page,
        pageSize: this.filters.pageSize,
        order: this.filters.order,
        field: this.filters.field,
        search: this.filters.search,
        all: this.filters.all,
        learning_type: this.filters.learning_type,
        idp_period_id: this.filters.idp_period_id,
        training_scope: this.filters.training_scope,
        training_category: this.filters.training_category,
        platform: this.filters.platform,
        training_method_id: this.filters.training_method_id,
        training_type: this.filters.training_type,
        competency_id: this.filters.competency_id,
        nik: this.filters.nik,
        type: "un-enrollment",
      };

      return cleanObject(raw);
    },

    async loadData() {
      if (!this.filters.nik) {
        this.resetIdpLearnings();
        return;
      }

      try {
        this.isLoading = true;

        const query = this.buildQueryFromFilters();

        await this.getIdpLearningReport(query);

        if (this.idpLearningReport.page) this.filters.page = this.idpLearningReport.page;
        if (this.idpLearningReport.limit) this.filters.pageSize = this.idpLearningReport.limit;
      } finally {
        this.isLoading = false;
      }
    },

    onSelectChange({ selectedRows }) {
      this.selectedRows = [...selectedRows];
    },

    onPageChange(payload) {
      let page = null;
      if (typeof payload === "number") page = payload;
      else if (payload?.currentPage !== undefined) page = payload.currentPage;
      else if (payload?.currentPageNumber !== undefined) page = payload.currentPageNumber;
      if (page === null) return;
      this.filters.page = page;
      this.loadData();
    },

    onPageChangeFromBv(page) {
      this.filters.page = page;
      this.loadData();
    },

    onPerPageChange(payload) {
      let per = null;
      if (typeof payload === "number") per = payload;
      else if (payload?.currentPerPage !== undefined) per = payload.currentPerPage;
      else if (payload?.perPage !== undefined) per = payload.perPage;
      if (per === null) return;
      this.filters.pageSize = per;
      this.filters.page = 1;
      this.loadData();
    },

    onSortChange(params) {
      if (!Array.isArray(params) || params.length === 0) return;
      const { field, type } = params[0] || {};
      const isReset = type === "none";
      this.filters = {
        ...this.filters,
        field: isReset ? null : field,
        order: isReset ? null : type || "asc",
        page: 1,
      };
      this.loadData();
    },

    onSearch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.filters.page = 1;
        this.loadData();
      }, 500);
    },

    clearFilter() {
      this.filters = { ...this.filters, search: "", page: 1 };
      this.loadData();
    },

    updateUrlQuery(filters) {
      const query = cleanObject({
        ...this.$route.query,
        page: filters.page,
        pageSize: filters.pageSize,
        order: filters.order,
        field: filters.field,
        search: filters.search,
        nik: filters.nik,
        idp_period_id: filters.idp_period_id,
        learning_type: filters.learning_type,
      });

      const current = JSON.stringify(this.$route.query);
      const next = JSON.stringify(query);

      if (current !== next) {
        this.$router.replace({ query }).catch(() => { });
      }
    },

    syncFiltersFromQuery() {
      const query = this.$route.query;
      const parsed = {
        page: Number(query.page) || 1,
        pageSize: Number(query.pageSize) || 10,
        order: query.order || null,
        field: query.field || null,
        search: query.search || "",
        nik: query.nik || this.filters.nik,
        idp_period_id: query.idp_period_id ? Number(query.idp_period_id) : this.filters.idp_period_id,
        learning_type: query.learning_type || this.filters.learning_type,
      };

      Object.keys(parsed).forEach((key) => {
        this.$set(this.filters, key, parsed[key]);
      });
    },

    handleApprove() {
      this.showConfirmationSubmitModal = true;
    },

    handleReject() {
      this.showRejectConfirmationModal = true;
    },

    async handleViewDetail(row) {
      this.isModalLoading = true;
      this.selectedId = row.id;
      this.showModal = true;

      const detail = await this.getStructuredLearningDetail({ id: row.id, query: { nik: this.filters.nik } });

      this.modalData = detail;

      this.$nextTick(() => {
        this.$bvModal.show("structured-learning-modal");
      });
      this.isModalLoading = false;
    },

    async approveUnEnrollment(ids) {
      if (ids.length === 0) return;

      try {
        await this.approveIdpLearning({
          query: { learning_type: "structured_learning" },
          data: { nik: this.filters.nik, ids, action: 'approve', type: 'un-enrollment' },
        });

        toast({ type: "success", title: "Success", message: "Pembatalan pendaftaran berhasil disetujui" });
        this.showConfirmationSubmitModal = false;
      } catch (error) {
        const msg = error?.response?.data?.error || error?.message || "Something went wrong.";
        toast({ type: "error", title: "Failed", message: msg });
      } finally {
        this.loadData();
      }
    },

    async rejectUnEnrollment(ids) {
      if (ids.length === 0) return;

      try {
        await this.approveIdpLearning({
          query: { learning_type: "structured_learning" },
          data: { nik: this.filters.nik, ids, action: 'reject', type: 'un-enrollment' },
        });

        toast({ type: "success", title: "Success", message: "Pembatalan pendaftaran berhasil ditolak" });
        this.showRejectConfirmationModal = false;
      } catch (error) {
        const msg = error?.response?.data?.error || error?.message || "Something went wrong.";
        toast({ type: "error", title: "Failed", message: msg });
      } finally {
        this.loadData();
      }
    },

    async loadSummaryLearning({ nik }) {
      const selectedNik = nik || this.filters.nik;

      if (!selectedNik) {
        this.resetIdpLearnings();
        return;
      }

      this.isLoadingSummary = true;

      const payload = {
        learning_type: "structured_learning",
        nik: selectedNik,
      };
      if (this.filters.idp_period_id) payload.idp_period_id = this.filters.idp_period_id;

      await this.getSummaryIdpLearningReport(payload);

      this.isLoadingSummary = false;
    },

    async loadIdpPeriod() {
      const idpPeriod = await this.getIdpPeriod();
      const now = new Date();

      this.filters.idp_period_id =
        idpPeriod.find((p) => new Date(p.start_date) <= now && new Date(p.end_date) >= now)?.id || null;

      this.idpPeriodOptions = idpPeriod.map((p) => ({
        text: this.formatPeriodLabel(p.start_date, p.end_date),
        value: p.id,
      }));
    },

    formatPeriodLabel(startDate, endDate) {
      if (!startDate || !endDate) return "";

      const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    },

    applyFilters(appliedFilters) {
      const cleaned = cleanObject(appliedFilters);

      Object.entries(cleaned).forEach(([key, val]) => {
        this.$set(this.filters, key, val);
      });

      this.$set(this.filters, "page", 1);
      this.updateActiveFilterCount();
      this.loadData();

      this.$bvModal.hide("modal-filter");
    },

    updateActiveFilterCount() {
      const { training_scope, training_category, platform, training_method_id, training_type, competency_id } = this.filters;
      const vals = [training_scope, training_category, platform, training_method_id, training_type, competency_id];

      this.activeFiltersCount = vals.filter((x) => x !== null && x !== undefined && x !== "").length;
    },

    resetFilters() {
      const reset = {
        training_scope: null,
        training_category: null,
        platform: null,
        training_method_id: null,
        training_type: null,
        competency_id: null,
        page: 1,
      };

      Object.entries(reset).forEach(([key, val]) => {
        this.$set(this.filters, key, val);
      });

      this.activeFiltersCount = 0;

      this.loadData();
    },

    clearSingleFilter(filterKey) {
      if (this.filters.hasOwnProperty(filterKey)) {
        this.$set(this.filters, filterKey, null);
        this.filters.page = 1;
        this.updateActiveFilterCount();
        this.loadData();
      }
    },

    buildSubordinateQuery(search = "", approvalOnly = false) {
      return cleanObject({
        page: 1,
        pageSize: 25,
        all: false,
        field: "fullname",
        order: "asc",
        search,
        bibOnly: true,
        approvalOnly,
        approvalType: "un-enrollment",
        learning_type: this.filters.learning_type,
        idp_period_id: this.filters.idp_period_id,
      });
    },

    async loadApprovalSubordinates(search = "", approvalOnly = false) {
      this.isLoadingEmployeeList = true;
      try {
        await this.getSubordinates(this.buildSubordinateQuery(search, approvalOnly));
      } finally {
        this.isLoadingEmployeeList = false;
      }

      if (this.filters.nik && !this.selectedSubordinate && search === "") {
        await this.getSubordinates(this.buildSubordinateQuery(this.filters.nik));
        if (!this.selectedSubordinate) this.filters.nik = null;
      }
    },

    selectApprovalEmployee(row) {
      if (!row?.nik) return;
      this.filters.nik = row.nik;
      this.position = row.position || null;
    },

    backToEmployeeList() {
      this.filters.nik = null;
      this.position = null;
      this.selectedRows = [];
      this.resetIdpLearnings();
    },

    onEmployeeListSearch() {
      clearTimeout(this._employeeListSearchTimer);
      this._employeeListSearchTimer = setTimeout(() => {
        this.loadApprovalSubordinates(this.employeeListSearch, true);
      }, 350);
    },

    onEmployeeSearch(search, loading) {
      clearTimeout(this._employeeSearchTimer);
      const term = String(search || "").trim();

      if (term && term.length < 2) return;

      loading?.(true);
      this._employeeSearchTimer = setTimeout(async () => {
        try {
          await this.loadApprovalSubordinates(term, false);
        } finally {
          loading?.(false);
        }
      }, 350);
    },
  },

  async mounted() {
    this.syncFiltersFromQuery();

    await Promise.all([
      this.loadIdpPeriod(),
      this.getTrainingMethods(),
      this.getTrainingTypes(),
      this.getCompetencies(),
    ]);

    await this.loadApprovalSubordinates("", true);
  },

  beforeDestroy() {
    clearTimeout(this._searchTimer);
    clearTimeout(this._employeeSearchTimer);
    clearTimeout(this._employeeListSearchTimer);
    this.resetIdpLearnings();
  },
};
